import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Home,
  Landmark,
  BrainCircuit,
  GitBranch,
  BarChart3,
  LogOut,
  // Sun,
  // Moon,
  Menu,
  X,
  User,
  Settings,
  Brain,
  Activity,
  Youtube,
} from "lucide-react";
import LogoutConfirm from "./LogoutConfirm";
import VideoGuideModal from "./VideoGuideModal";
import API from "../../api/axios";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [username, setUsername] = useState("user@gmail.com");
  const [avatar, setAvatar] = useState(localStorage.getItem("User-Avatar") || "");
  const [balance, setBalance] = useState(0.0);
  const [hold, setHold] = useState(0.0);
  const navigate = useNavigate();
  const location = useLocation();

  const isLoggedIn = Boolean(localStorage.getItem("Access-Token"));
  const userRole = localStorage.getItem("User-Role") || "Trader";

  useEffect(() => {
    setOpen(false);
    setDropdownOpen(false);
  }, [location.key]);

  useEffect(() => {
    if (isLoggedIn) {
      API.get("/users/me")
        .then((res) => {
          setUsername(res.data.username || res.data.email);
          setBalance(res.data.credits_balance || 0.0);
          setHold(res.data.hold_credits || 0.0);
          if (res.data.avatar) {
            setAvatar(res.data.avatar);
            localStorage.setItem("User-Avatar", res.data.avatar);
          }
        })
        .catch((err) => console.error(err));
    }
  }, [isLoggedIn, location.key, userRole]);

  // // Apply theme on mount and when toggled
  // useEffect(() => {
  //   if (darkMode) {
  //     document.documentElement.classList.add("dark");
  //     localStorage.setItem("theme", "dark");
  //   } else {
  //     document.documentElement.classList.remove("dark");
  //     localStorage.setItem("theme", "light");
  //   }
  // }, [darkMode]);

  const logout = () => {
    localStorage.removeItem("Access-Token");
    localStorage.removeItem("User-Role");
    localStorage.removeItem("User-Id");
    localStorage.removeItem("User-Avatar");
    setAvatar("");
    navigate("/login");
  };

  const linkClasses = ({ isActive }) =>
    `flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-all duration-200
    ${isActive
      ? "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 font-semibold shadow-sm"
      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-500 dark:hover:text-blue-300"
    }`;



  return (
    <nav className="bg-white dark:bg-gray-900 shadow-md p-4 flex items-center justify-between sticky top-0 z-50">
      {/* Logo */}
      <NavLink
        to="/"
        className="text-xl font-bold text-blue-600 dark:text-blue-400 tracking-wide"
      >
        Algo Masters
      </NavLink>

      {/* Desktop Menu */}
      <div className="hidden md:flex items-center space-x-2">
        {isLoggedIn ? (
          <>
            <NavLink to="/dashboard" className={linkClasses}>
              <Home size={16} /> Dashboard
            </NavLink>

            <NavLink to="/brokers" className={linkClasses}>
              <Landmark size={16} /> Brokers
            </NavLink>

            <NavLink to="/bots" className={linkClasses}>
              <Brain size={16} /> AI Bot
            </NavLink>

            <NavLink to="/strategies" className={linkClasses}>
              <BrainCircuit size={16} /> Strategies
            </NavLink>

            <NavLink to="/legs" className={linkClasses}>
              <GitBranch size={16} /> Legs
            </NavLink>

            <NavLink to="/reports" className={linkClasses}>
              <BarChart3 size={16} /> Reports
            </NavLink>
            {(isLoggedIn) && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50/50 dark:bg-blue-900/30 border border-blue-100/50 dark:border-blue-900/50 text-xs font-semibold text-blue-600 dark:text-blue-300">
                <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider font-semibold mr-1">Bal:</span>
                ${balance.toFixed(2)}
                {hold !== 0 && (
                  <span className="text-red-500 dark:text-red-400 font-bold ml-1">
                    (Hold: ${hold.toFixed(2)})
                  </span>
                )}
              </div>
            )}

            {/* Video Tutorial Button */}
            <button
              type="button"
              onClick={() => setVideoModalOpen(true)}
              className="ml-2 flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 dark:bg-red-950/30 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 border border-red-200/80 dark:border-red-900/50 transition-colors"
              title="Watch video tutorials and platform guide"
            >
              <Youtube size={14} />
              <span className="hidden xl:inline">Video Guide</span>
            </button>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="ml-4 flex items-center gap-2 p-1 rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition focus:outline-none"
                aria-label="User profile menu"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-blue-400 text-white flex items-center justify-center text-xs font-bold shadow-md overflow-hidden">
                  {avatar ? (
                    <img
                      src={avatar}
                      alt={username}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    userRole.substring(0, 2).toUpperCase()
                  )}
                </div>
              </button>

              {dropdownOpen && (
                <>
                  {/* Transparent background overlay to dismiss the dropdown */}
                  <div 
                    className="fixed inset-0 z-40 cursor-default" 
                    onClick={() => setDropdownOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-2 z-50 animate-fade-in">
                    {/* User summary header */}
                    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                      {avatar ? (
                        <img
                          src={avatar}
                          alt={username}
                          referrerPolicy="no-referrer"
                          className="w-8 h-8 rounded-full object-cover border border-blue-400/40 shadow-sm flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-blue-400 text-white flex items-center justify-center text-xs font-bold shadow-sm flex-shrink-0">
                          {userRole.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider font-semibold">Logged In As</p>
                        <p className="text-xs font-bold text-gray-800 dark:text-gray-200 truncate mt-0.5">
                          {username}
                        </p>
                      </div>
                    </div>

                    <div className="p-1">
                      <NavLink
                        to="/profile"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                      >
                        <User size={13} /> View Profile
                      </NavLink>
                      <NavLink
                        to="/profile"
                        onClick={() => {
                          setDropdownOpen(false);
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                      >
                        <Settings size={13} /> Trade Settings
                      </NavLink>
                      <NavLink
                        to="/system-health"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-lg transition font-medium"
                      >
                        <Activity size={13} /> System Health
                      </NavLink>
                    </div>

                    <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>

                    <div className="p-1">
                      <button
                        onClick={() => {
                          setDropdownOpen(false);
                          setShowLogoutConfirm(true);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition text-left"
                      >
                        <LogOut size={13} /> Logout
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <NavLink to="/about" className="text-sm px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-blue-500 font-medium transition">
              About
            </NavLink>
            <NavLink to="/contact" className="text-sm px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-blue-500 font-medium transition">
              Contact
            </NavLink>
            <button
              type="button"
              onClick={() => setVideoModalOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 dark:bg-red-950/30 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 border border-red-200/80 dark:border-red-900/50 transition-colors"
            >
              <Youtube size={14} /> Video Guide
            </button>
            <NavLink to="/login" className="text-sm px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-blue-500 font-bold transition">
              Sign In
            </NavLink>
            <NavLink to="/register" className="text-sm px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-sm transition">
              Create Account
            </NavLink>
          </>
        )}
      </div>

      {/* Mobile Hamburger */}
      <button
        className="md:hidden text-gray-700 dark:text-gray-300"
        onClick={() => setOpen(true)}
        aria-label="Open Menu"
      >
        <Menu size={26} />
      </button>

      {/* Right-side Mobile Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white dark:bg-gray-900 shadow-xl z-50 transform transition-transform duration-300 ease-in-out rounded-l-2xl
        ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Close button */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            Menu
          </h2>
          <button onClick={() => setOpen(false)} aria-label="Close Menu">
            <X size={24} />
          </button>
        </div>

        {/* Mobile Links */}
        <div className="flex flex-col space-y-2 p-4">
          {isLoggedIn ? (
            <>
              {/* User mobile header info */}
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 mb-2">
                {avatar ? (
                  <img
                    src={avatar}
                    alt={username}
                    referrerPolicy="no-referrer"
                    className="w-10 h-10 rounded-full object-cover border border-blue-400/40 shadow-sm flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-600 to-blue-400 text-white flex items-center justify-center text-xs font-bold shadow-sm flex-shrink-0">
                    {userRole.substring(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-gray-800 dark:text-gray-200 truncate">{username}</p>
                  <span className="text-[10px] text-blue-500 font-semibold">{userRole}</span>
                </div>
              </div>

              {(userRole.toUpperCase() === "USER" || userRole.toUpperCase() === "TRADER") && (
                <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-blue-50/50 dark:bg-blue-900/30 border border-blue-100/50 dark:border-blue-900/50 text-xs font-semibold text-blue-600 dark:text-blue-300 mb-2">
                  <span>Balance</span>
                  <span>
                    ${balance.toFixed(2)}
                    {hold !== 0 && (
                      <span className="text-red-500 dark:text-red-400 font-bold ml-1">
                        (Hold: ${hold.toFixed(2)})
                      </span>
                    )}
                  </span>
                </div>
              )}
              <NavLink to="/dashboard" className={linkClasses}>
                <Home size={16} /> Dashboard
              </NavLink>
              <NavLink to="/brokers" className={linkClasses}>
                <Landmark size={16} /> Brokers
              </NavLink>
              <NavLink to="/bots" className={linkClasses}>
                <Brain size={16} /> AI Bot
              </NavLink>
              <NavLink to="/strategies" className={linkClasses}>
                <BrainCircuit size={16} /> Strategies
              </NavLink>
              <NavLink to="/legs" className={linkClasses}>
                <GitBranch size={16} /> Legs
              </NavLink>
              <NavLink to="/reports" className={linkClasses}>
                <BarChart3 size={16} /> Reports
              </NavLink>
              <NavLink to="/profile" className={linkClasses}>
                <User size={16} /> Profile & Settings
              </NavLink>
              <NavLink to="/system-health" className={linkClasses}>
                <Activity size={16} className="text-emerald-500" /> System Health
              </NavLink>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setVideoModalOpen(true);
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition text-left font-semibold text-xs"
              >
                <Youtube size={16} /> Video Guide & Tutorials
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  setShowLogoutConfirm(true);
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/50 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-800 transition text-left"
              >
                <LogOut size={16} /> Logout
              </button>
            </>
          ) : (
            <>
              <NavLink to="/about" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition font-medium">
                About Us
              </NavLink>
              <NavLink to="/contact" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition font-medium">
                Contact & Support
              </NavLink>
              <NavLink to="/risk-disclaimer" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20 rounded-lg transition font-medium">
                Risk Disclaimer
              </NavLink>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setVideoModalOpen(true);
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition text-left font-semibold text-xs"
              >
                <Youtube size={16} /> Video Guide & Tutorials
              </button>
              <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
              <NavLink to="/login" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition font-bold">
                Sign In
              </NavLink>
              <NavLink to="/register" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 px-3 py-2.5 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition font-bold shadow-md">
                Create Account
              </NavLink>
            </>
          )}
        </div>

          {/* Dark Mode Toggle (mobile) */}
          {/* <button
            onClick={() => setDarkMode(!darkMode)}
            className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
          >
            {darkMode ? (
              <>
                <Sun size={18} className="text-yellow-400" /> Light Mode
              </>
            ) : (
              <>
                <Moon size={18} className="text-gray-600 dark:text-gray-300" />{" "}
                Dark Mode
              </>
            )}
          </button> */}
      </div>

      {/* Overlay */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black bg-opacity-40 md:hidden z-40"
        />
      )}

      {/* Video Guide Modal */}
      <VideoGuideModal
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        guideKey="platform"
      />

      {/* Logout Confirmation Modal */}
      <LogoutConfirm
        isOpen={showLogoutConfirm}
        onCancel={() => setShowLogoutConfirm(false)}
        onConfirm={logout}
      />
    </nav>
  );
}
